import React, {
    useState,
    useEffect,
} from 'react'
import {
    Zap,
    Loader2,
    AlertTriangle,
} from 'lucide-react'
import { DurationButton } from './DurationButton.js'
import { ErrorModal } from './ErrorModal.js'
import { SuccessModal } from './SuccessModal.js'
import {
    Room,
    RoomModel,
} from '../../common/models/RoomModel.js'
import { Booking } from '../../common/models/BookingModel.js'
import { roundMinutesToNearest5Or0 } from '../../common/utils/utils.js'

export interface QuickBookProps {
    rooms: RoomModel[]
}

export function QuickBook(props: QuickBookProps) {
    const { rooms } = props
    const [duration, setDuration] = useState<number>(15)
    const [disabledDurations, setDisabledDurations] = useState<number[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [bookedRoom, setBookedRoom] = useState<{
        roomName: string;
        duration: number;
        startTime: Date;
        endTime: Date;
    } | null>(null)

    // Update selected duration if current selection becomes unavailable
    useEffect(() => {
        if (disabledDurations.includes(duration)) {
            // Find the first available duration
            const availableDurations = [15, 30, 45, 55].filter(d => !disabledDurations.includes(d))
            if (availableDurations.length > 0) {
                setDuration(availableDurations[0])
            }
        }
    }, [disabledDurations, duration])

    useEffect(() => {
        checkAvailabilityForAllDurations(rooms)
    }, [rooms])

    // Check availability for all durations
    const checkAvailabilityForAllDurations = (roomModels: RoomModel[]) => {
        const durations = [15, 30, 45, 55]
        const unavailableDurations: number[] = []

        for (const dur of durations) {
            if (!isDurationAvailable(roomModels, dur)) {
                unavailableDurations.push(dur)
            }
        }

        setDisabledDurations(unavailableDurations)
    }

    // Check if a specific duration is available in any room
    const isDurationAvailable = (roomModels: RoomModel[], durationToCheck: number): boolean => {
        const startDateTime = new Date()
        const rawEndDateTime = new Date(startDateTime.getTime() + durationToCheck * 60 * 1000)
        const endDateTime = roundMinutesToNearest5Or0(rawEndDateTime)

        // Check if at least one room is available for this duration
        return roomModels.some(room => room.isAvailable(startDateTime, endDateTime))
    }

    const handleQuickBook = async () => {
        setIsLoading(true)
        setError(null)
        setBookedRoom(null)

        try {
            const response = await fetch('/api/v1/rooms/quick-book', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ duration }),
            })

            if (!response.ok) {
                throw new Error('No available rooms found for the specified duration')
            }

            const data = await response.json() as { success: boolean, room: Room, event: Booking }

            if (data.success && data.event) {
                // Parse the event data
                const startTime = new Date(data.event.startTime)
                const endTime = new Date(data.event.endTime)
                const actualDuration = Math.round((endTime.getTime() - startTime.getTime()) / (60 * 1000))

                // Set the booked room information
                setBookedRoom({
                    roomName: data.room.name || (data.event.roomId ? `Room ${data.event.roomId}` : 'Unknown Room'),
                    duration: actualDuration,
                    startTime,
                    endTime,
                })

                // Show the success modal
                setShowSuccessModal(true)
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to book room')
            setShowErrorModal(true)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <>
            <div className="px-6 pt-6 pb-6 items-center size-full">
                <div className="p-6 bg-white rounded-[20px] max-w-[370px] md:max-w-full flex flex-col md:flex-row md:rounded-[800px] mx-auto gap-4">
                    <div className="flex flex-row justify-center md:justify-start gap-1 md:gap-4 md:flex-1 overflow-x-auto">
                        <DurationButton
                            duration={15}
                            isSelected={duration === 15}
                            isDisabled={disabledDurations.includes(15)}
                            onClick={() => setDuration(15)}
                        />
                        <DurationButton
                            duration={30}
                            isSelected={duration === 30}
                            isDisabled={disabledDurations.includes(30)}
                            onClick={() => setDuration(30)}
                        />
                        <DurationButton
                            duration={45}
                            isSelected={duration === 45}
                            isDisabled={disabledDurations.includes(45)}
                            onClick={() => setDuration(45)}
                        />
                        <DurationButton
                            duration={55}
                            isSelected={duration === 55}
                            isDisabled={disabledDurations.includes(55)}
                            onClick={() => setDuration(55)}
                        />
                    </div>
                    <button
                        className={`h-12 px-6 py-3 rounded-full flex items-center justify-center gap-2 transition-transform w-full sm:w-auto ${
                            isLoading || disabledDurations.includes(duration)
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-black text-white'
                        }`}
                        onClick={() => {
                            void handleQuickBook()
                        }}
                        disabled={isLoading || disabledDurations.includes(duration)}
                    >
                        {isLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                            <Zap className="w-5 h-5" />
                        )}
                        <span className="font-bold whitespace-nowrap">
                    {isLoading ? 'Booking...' : 'Book me any room now!'}
                </span>
                    </button>
                    <ErrorModal
                        isOpen={showErrorModal}
                        onClose={() => setShowErrorModal(false)}
                        errorMessage={error}
                    />

                    {bookedRoom && (
                        <SuccessModal
                            isOpen={showSuccessModal}
                            onClose={() => setShowSuccessModal(false)}
                            roomName={bookedRoom.roomName}
                            duration={bookedRoom.duration}
                            startTime={bookedRoom.startTime}
                            endTime={bookedRoom.endTime}
                        />
                    )}
                </div>
            </div>
            {disabledDurations.length > 0 && (
                <div className="flex text-center w-full items-center text-[#e17100] text-xs mb-6 justify-center">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    <span>Some durations not available due to upcoming meetings</span>
                </div>
            )}
        </>
    )
}
